import { BREAKPOINTS } from "@/settings";
import { clsx } from "clsx";
import { useEffect, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

type Props = {
  onChange: (isActive: boolean) => void;
  onClick?: () => void;
  isActive: boolean;
  disabled?: boolean;
  locked?: boolean;
  isSmall?: boolean;
  isOnSbcPages?: boolean;
  activeColor?: string;
};

export const ToggleSwitch = ({
  onChange,
  onClick,
  isActive: isActiveProp,
  disabled,
  locked = false,
  isSmall = false,
  isOnSbcPages,
  activeColor,
}: Props) => {
  const [isActive, setIsActive] = useState(isActiveProp);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  useEffect(() => {
    setIsActive(isActiveProp);
  }, [isActiveProp]);

  const circleStyle = {
    transition: "transform 0.15s ease-in-out", // Reduced from 0.2s for snappier feel
    transform: isActive
      ? isMobile && isOnSbcPages
        ? "translateX(78%)"
        : "translateX(80%)"
      : "translateX(0%)",
    boxShadow: "0px 1px 1px 0px rgba(0, 0, 0, 0.25)",
  };

  const handleToggle = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (locked) {
      onChange(true);
      return;
    }

    if (disabled) return;

    onClick?.();
    const next = !isActive;
    setIsActive(next);
    onChange(next);
  };

  return (
    <div
      className={clsx(
        "flex items-center rounded-full p-1 cursor-pointer",
        isSmall ? "w-9 h-5" : "w-10 h-6",
        isMobile && isOnSbcPages ? "w-12 h-8" : "w-10 h-6",
        isActive ? (activeColor ?? "bg-primary-blue") : "bg-[#49535F]",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      )}
      style={{
        boxShadow: "0px 1px 1px 0px rgba(0, 0, 0, 0.25) inset",
        // Add touch-action for better mobile performance
        touchAction: "manipulation",
      }}
      onClick={(e) => {
        if (!isMobile) {
          handleToggle(e);
        }
      }}
      onTouchEnd={(e) => {
        if (disabled) return;
        handleToggle(e);
      }}
    >
      <div
        style={circleStyle}
        className={clsx(
          "rounded-full shadow-md bg-white -ml-[2px]",
          isSmall ? "w-4 h-4" : "w-5 h-5",
          isMobile && isOnSbcPages ? "w-6 h-6" : "w-5 h-5",
          disabled && "opacity-60",
        )}
      />
    </div>
  );
};
